import { useState, useEffect } from 'react'
import { useFilters } from '../contexts/FilterContext'
import { api, CorretorRanking } from '../services/api'
import { BarChart3, Clock, ThumbsUp, Users, X } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import styles from './Corretores.module.css'

export function Corretores() {
  const { queryParams, filtersVersion } = useFilters()
  const [corretores, setCorretores] = useState<CorretorRanking[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showComparison, setShowComparison] = useState(false)
  const [selectedCorretores, setSelectedCorretores] = useState<number[]>([])

  // KPI Calculations
  const [kpis, setKpis] = useState({
    tempoMedioResposta: 0,
    tempoContatoVenda: 0,
    taxaConversaoMedia: 0,
    corretoresAtivos: 0,
    totalCorretores: 0,
    slaAtendimento: 0,
  })

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Carrega ranking de corretores e dados de produtividade em paralelo
        const [rankingData, produtividadeData] = await Promise.all([
          api.getCorretoresRanking(`${queryParams}&limit=50`),
          api.getProdutividade(queryParams).catch(() => null)
        ])

        setCorretores(rankingData)

        // Calculate KPIs from ranking
        const ativos = rankingData.filter((c: CorretorRanking) => c.total_leads > 0).length
        const taxaMedia = rankingData.length > 0
          ? rankingData.reduce((acc: number, c: CorretorRanking) => acc + c.taxa_conversao, 0) / rankingData.length
          : 0

        // Tempo médio de resposta da equipe (já calculado pela API em minutos)
        const tempoRespostaMin = produtividadeData?.kpis?.tempo_medio_equipe || 0
        const tempoRespostaHoras = tempoRespostaMin > 0 ? tempoRespostaMin / 60 : 0

        setKpis({
          tempoMedioResposta: tempoRespostaHoras,
          tempoContatoVenda: produtividadeData?.kpis?.carga_media || 0,
          taxaConversaoMedia: taxaMedia,
          corretoresAtivos: produtividadeData?.kpis?.corretores_ativos || ativos,
          totalCorretores: produtividadeData?.kpis?.total_corretores || rankingData.length,
          slaAtendimento: produtividadeData?.kpis?.sla_atendimento || 0,
        })
      } catch (err) {
        setError('Erro ao carregar dados dos corretores')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [queryParams, filtersVersion])

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`
    }
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(0)}K`
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const getMedalha = (posicao: number) => {
    if (posicao === 1) return { text: '1', className: styles.gold }
    if (posicao === 2) return { text: '2', className: styles.silver }
    if (posicao === 3) return { text: '3', className: styles.bronze }
    return { text: posicao.toString(), className: '' }
  }

  const getTimeBadgeClass = (time: string) => {
    if (time === 'House') return styles.timeHouse
    if (time === 'Alfa') return styles.timeAlfa
    if (time === 'Parcerias') return styles.timeParcerias
    return ''
  }

  const toggleCorretorSelection = (id: number) => {
    setSelectedCorretores(prev => {
      if (prev.includes(id)) {
        return prev.filter(cId => cId !== id)
      }
      if (prev.length < 3) {
        return [...prev, id]
      }
      return prev
    })
  }

  const selectedCorretoresData = corretores.filter(c => selectedCorretores.includes(c.id))

  // Chart data for comparison
  const vendasChartData = selectedCorretoresData.map(c => ({
    name: c.nome.split(' ')[0],
    vendas: c.total_vendas,
  }))

  const conversaoChartData = selectedCorretoresData.map(c => ({
    name: c.nome.split(' ')[0],
    conversao: c.taxa_conversao,
  }))

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Performance Corretores</h1>
            <p className={styles.pageSubtitle}>Analise de desempenho por corretor</p>
          </div>
        </div>
        <div className={styles.errorState}>
          <p>{error}</p>
          <p>Verifique se a API esta funcionando corretamente.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Carregando dados dos corretores...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Performance Corretores</h1>
          <p className={styles.pageSubtitle}>Analise completa de desempenho por corretor</p>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.btnPrimary}
            onClick={() => setShowComparison(!showComparison)}
          >
            <BarChart3 size={16} />
            {showComparison ? 'Fechar Comparacao' : 'Comparar Corretores'}
          </button>
        </div>
      </div>

      {/* Comparison Section */}
      {showComparison && (
        <section className={`${styles.section} ${styles.comparisonSection}`}>
          <div className={styles.comparisonHeader}>
            <h2 className={styles.sectionTitle}>Comparacao de Corretores</h2>
            <button className={styles.btnOutline} onClick={() => setShowComparison(false)}>
              <X size={16} />
              Fechar
            </button>
          </div>

          <div className={styles.corretorSelector}>
            <label>Selecione ate 3 corretores para comparar:</label>
            <div className={styles.corretorChips}>
              {corretores.slice(0, 15).map(corretor => (
                <span
                  key={corretor.id}
                  className={`${styles.corretorChip} ${selectedCorretores.includes(corretor.id) ? styles.selected : ''}`}
                  onClick={() => toggleCorretorSelection(corretor.id)}
                >
                  {corretor.nome}
                </span>
              ))}
            </div>
          </div>

          {selectedCorretoresData.length > 0 && (
            <>
              {/* Comparison Cards */}
              <div className={styles.comparisonCards}>
                {selectedCorretoresData.map(corretor => (
                  <div key={corretor.id} className={styles.comparisonCard}>
                    <div className={styles.comparisonCardHeader}>
                      <div>
                        <h3 className={styles.comparisonCardName}>{corretor.nome}</h3>
                        <span className={styles.comparisonCardImob}>{corretor.imobiliaria}</span>
                      </div>
                      <span className={`${styles.timeBadge} ${getTimeBadgeClass(corretor.time)}`}>
                        {corretor.time}
                      </span>
                    </div>
                    <div className={styles.comparisonCardMetrics}>
                      <div className={styles.comparisonMetric}>
                        <span className={styles.comparisonMetricLabel}>Leads</span>
                        <span className={styles.comparisonMetricValue}>{corretor.total_leads}</span>
                      </div>
                      <div className={styles.comparisonMetric}>
                        <span className={styles.comparisonMetricLabel}>Vendas</span>
                        <span className={styles.comparisonMetricValue}>{corretor.total_vendas}</span>
                      </div>
                      <div className={styles.comparisonMetric}>
                        <span className={styles.comparisonMetricLabel}>Conversao</span>
                        <span className={styles.comparisonMetricValue}>{corretor.taxa_conversao}%</span>
                      </div>
                      <div className={styles.comparisonMetric}>
                        <span className={styles.comparisonMetricLabel}>VGV</span>
                        <span className={styles.comparisonMetricValue}>{formatCurrency(corretor.vgv)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Comparison Charts */}
              <div className={styles.comparisonCharts}>
                <div className={styles.chartCard}>
                  <div className={styles.chartHeader}>
                    <h3 className={styles.chartTitle}>Comparativo de Vendas</h3>
                    <p className={styles.chartSubtitle}>Quantidade de vendas por corretor</p>
                  </div>
                  <div className={styles.chartContainer}>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={vendasChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip />
                        <Bar dataKey="vendas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className={styles.chartCard}>
                  <div className={styles.chartHeader}>
                    <h3 className={styles.chartTitle}>Comparativo de Conversao</h3>
                    <p className={styles.chartSubtitle}>Taxa de conversao Lead para Venda</p>
                  </div>
                  <div className={styles.chartContainer}>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={conversaoChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip formatter={(value: number) => [`${value}%`, 'Conversao']} />
                        <Bar dataKey="conversao" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Comparison Table */}
              <div className={styles.tableContainer}>
                <table className={`${styles.table} ${styles.comparisonTable}`}>
                  <thead>
                    <tr>
                      <th>Metrica</th>
                      {selectedCorretoresData.map(c => (
                        <th key={c.id}>{c.nome.split(' ')[0]}</th>
                      ))}
                      <th>Media Equipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Leads Recebidos</td>
                      {selectedCorretoresData.map(c => (
                        <td key={c.id}>{c.total_leads}</td>
                      ))}
                      <td>{corretores.length > 0 ? Math.round(corretores.reduce((acc, c) => acc + c.total_leads, 0) / corretores.length) : 0}</td>
                    </tr>
                    <tr>
                      <td>Pre-Cadastros</td>
                      {selectedCorretoresData.map(c => (
                        <td key={c.id}>{c.total_pre_cadastros}</td>
                      ))}
                      <td>{corretores.length > 0 ? Math.round(corretores.reduce((acc, c) => acc + c.total_pre_cadastros, 0) / corretores.length) : 0}</td>
                    </tr>
                    <tr>
                      <td>Reservas</td>
                      {selectedCorretoresData.map(c => (
                        <td key={c.id}>{c.total_reservas}</td>
                      ))}
                      <td>{corretores.length > 0 ? Math.round(corretores.reduce((acc, c) => acc + c.total_reservas, 0) / corretores.length) : 0}</td>
                    </tr>
                    <tr>
                      <td>Vendas</td>
                      {selectedCorretoresData.map(c => (
                        <td key={c.id}>{c.total_vendas}</td>
                      ))}
                      <td>{corretores.length > 0 ? Math.round(corretores.reduce((acc, c) => acc + c.total_vendas, 0) / corretores.length) : 0}</td>
                    </tr>
                    <tr>
                      <td>Taxa de Conversao</td>
                      {selectedCorretoresData.map(c => (
                        <td key={c.id}>{c.taxa_conversao}%</td>
                      ))}
                      <td>{corretores.length > 0 ? (corretores.reduce((acc, c) => acc + c.taxa_conversao, 0) / corretores.length).toFixed(1) : '0.0'}%</td>
                    </tr>
                    <tr>
                      <td>Nota</td>
                      {selectedCorretoresData.map(c => (
                        <td key={c.id}>{c.nota}</td>
                      ))}
                      <td>{corretores.length > 0 ? (corretores.reduce((acc, c) => acc + c.nota, 0) / corretores.length).toFixed(0) : 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      {/* KPIs de Performance */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Indicadores de Performance da Equipe</h2>
        <div className={styles.kpiGrid4}>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIconSm} ${styles.primary}`}>
              <Clock size={16} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Tempo Medio Resposta Lead</span>
              <span className={styles.kpiValueSm}>{kpis.tempoMedioResposta.toFixed(1)} h</span>
              <span className={`${styles.kpiMeta} ${kpis.tempoMedioResposta <= 24 ? styles.success : styles.neutral}`}>
                {kpis.tempoMedioResposta <= 24 ? 'Dentro do SLA' : 'Acima do esperado'}
              </span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIconSm} ${styles.secondary}`}>
              <BarChart3 size={16} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Carga Media por Corretor</span>
              <span className={styles.kpiValueSm}>{kpis.tempoContatoVenda.toFixed(0)} leads</span>
              <span className={styles.kpiMeta}>Leads atribuidos</span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIconSm} ${styles.success}`}>
              <ThumbsUp size={16} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Taxa Conversao Media</span>
              <span className={styles.kpiValueSm}>{kpis.taxaConversaoMedia.toFixed(1)}%</span>
              <span className={`${styles.kpiMeta} ${kpis.taxaConversaoMedia >= 10 ? styles.success : styles.neutral}`}>
                {kpis.taxaConversaoMedia >= 10 ? 'Acima da media' : 'Media do mercado'}
              </span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIconSm} ${styles.warning}`}>
              <Users size={16} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Corretores Ativos</span>
              <span className={styles.kpiValueSm}>{kpis.corretoresAtivos}</span>
              <span className={styles.kpiMeta}>de {kpis.totalCorretores} totais</span>
            </div>
          </div>
        </div>
      </section>

      {/* Ranking Table */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Ranking de Corretores</h2>
        <div className={styles.tableContainer}>
          {corretores.length === 0 ? (
            <div className={styles.emptyState}>
              <p>Nenhum corretor encontrado no periodo selecionado</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Corretor</th>
                  <th>Time</th>
                  <th>Leads</th>
                  <th>Pre-Cadastros</th>
                  <th>Reservas</th>
                  <th>Vendas</th>
                  <th>VGV</th>
                  <th>Conversao</th>
                  <th>Nota</th>
                </tr>
              </thead>
              <tbody>
                {corretores.map((corretor) => {
                  const medalha = getMedalha(corretor.posicao)
                  return (
                    <tr key={corretor.id}>
                      <td>
                        <span className={`${styles.rankBadge} ${medalha.className}`}>
                          {medalha.text}
                        </span>
                      </td>
                      <td className={styles.nameCell}>
                        <div>
                          <span className={styles.corretorNome}>{corretor.nome}</span>
                          <span className={styles.corretorImob}>{corretor.imobiliaria}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`${styles.timeBadge} ${getTimeBadgeClass(corretor.time)}`}>
                          {corretor.time}
                        </span>
                      </td>
                      <td>{corretor.total_leads}</td>
                      <td>{corretor.total_pre_cadastros}</td>
                      <td>{corretor.total_reservas}</td>
                      <td className={styles.vendas}>{corretor.total_vendas}</td>
                      <td className={styles.vgv}>{formatCurrency(corretor.vgv)}</td>
                      <td>
                        <span className={`${styles.conversionBadge} ${corretor.taxa_conversao >= 15 ? styles.success : ''}`}>
                          {corretor.taxa_conversao}%
                        </span>
                      </td>
                      <td>
                        <span
                          className={`${styles.notaBadge} ${
                            corretor.nota >= 70
                              ? styles.notaGood
                              : corretor.nota >= 40
                              ? styles.notaNeutral
                              : styles.notaLow
                          }`}
                        >
                          {corretor.nota}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  )
}
